import type { PortfolioTemplate } from '@interfaces/financialProduct';
import { useEffect, useState } from 'react';
import { ChoosePortfolioType } from './components/choosePortfolioType';
import { CreatePortfolioTemplate } from './components/createPortfolioTemplate';
import { SelectPortfolioTemplate } from './components/selectPortfolioTemplate';
import {
  type CreateModelPortfolioOptions,
  type ModelPortfolioData,
  SegmentType,
} from './createModelPortfolio.types';

export type CreateModelPortfolioVisualProps = {
  options: CreateModelPortfolioOptions;
  modelPortfolioData: ModelPortfolioData;
  loading: boolean;
  updateModelPortfolio: (updates: Partial<ModelPortfolioData>) => void;
  continueFunc: (
    modelPortfolioData: ModelPortfolioData,
  ) => void | Promise<void>;
  workflowCompletion?: {
    workflow?: {
      id: string;
      name: string;
    };
    id: string;
    objectId: string;
    objectType: string;
  };
  workflowData: any;
  setWorkflowData: (data: any) => void;
  onBack?: () => void;
  navigateToSegment: (segment: string) => void;
};

export const CreateModelPortfolioVisual = ({
  options,
  modelPortfolioData,
  loading,
  updateModelPortfolio,
  continueFunc,
  workflowCompletion,
  workflowData,
  setWorkflowData,
  onBack,
  navigateToSegment,
}: CreateModelPortfolioVisualProps) => {
  const [currentSegment, setCurrentSegment] = useState<SegmentType>(
    workflowData.currentSegment || SegmentType.CHOOSE_PORTFOLIO_TYPE,
  );

  useEffect(() => {
    if (workflowData.currentSegment) {
      setCurrentSegment(workflowData.currentSegment);
    }
  }, [workflowData.currentSegment]);

  useEffect(() => {
    if (
      currentSegment === SegmentType.CREATE_PORTFOLIO_TEMPLATE ||
      currentSegment === SegmentType.SELECT_PORTFOLIO_TEMPLATE
    ) {
      setWorkflowData({
        ...workflowData,
        modelPortfolioDetails: {
          ...workflowData.modelPortfolioDetails,
          templateCreationInProgress: true,
        },
      });
    }
  }, [currentSegment, workflowData, setWorkflowData]);

  const handleSaveForLater = (template: PortfolioTemplate) => {
    setWorkflowData({
      ...workflowData,
      currentSegment: SegmentType.SELECT_PORTFOLIO_TEMPLATE,
    });
    updateModelPortfolio({
      ...modelPortfolioData,
      portfolioTemplate: template,
    });
    onBack?.();
  };

  const renderSegment = (segment: SegmentType) => {
    switch (segment) {
      case SegmentType.CREATE_PORTFOLIO_TEMPLATE:
        return (
          <CreatePortfolioTemplate
            options={options.createTemplate}
            loading={loading}
            workflowCompletion={workflowCompletion}
            onComplete={(template: PortfolioTemplate) =>
              continueFunc({
                ...modelPortfolioData,
                portfolioTemplate: template,
              })
            }
            onSaveForLater={handleSaveForLater}
          />
        );

      case SegmentType.SELECT_PORTFOLIO_TEMPLATE:
        return (
          <SelectPortfolioTemplate
            options={options.selectTemplate}
            loading={loading}
            continueFunc={() => continueFunc(modelPortfolioData)}
            workflowCompletion={workflowCompletion}
            selectedPortfolioTemplate={
              modelPortfolioData.portfolioTemplate || undefined
            }
            onTemplateSelect={(template: PortfolioTemplate) => {
              updateModelPortfolio({
                ...modelPortfolioData,
                portfolioTemplate: template,
              });
            }}
            onCreateNew={() =>
              navigateToSegment(SegmentType.CREATE_PORTFOLIO_TEMPLATE)
            }
          />
        );

      case SegmentType.CHOOSE_PORTFOLIO_TYPE:
      default:
        return (
          <ChoosePortfolioType
            options={options.choosePortfolioType}
            modelPortfolioData={modelPortfolioData}
            loading={loading}
            updateModelPortfolio={updateModelPortfolio}
            onContinue={async () => {
              if (modelPortfolioData.creationType === 'FROM_TEMPLATE') {
                navigateToSegment(SegmentType.SELECT_PORTFOLIO_TEMPLATE);
              } else {
                const fromScratch: ModelPortfolioData = {
                  ...modelPortfolioData,
                  portfolioTemplate: undefined,
                  creationType: 'FROM_SCRATCH',
                };
                updateModelPortfolio(fromScratch);
                await Promise.resolve(continueFunc(fromScratch));
              }
            }}
            workflowCompletion={workflowCompletion}
          />
        );
    }
  };

  return renderSegment(currentSegment);
};
